<script lang="ts" setup>
import type { ColorInputWithoutInstance } from "tinycolor2";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import type { ColorFormat } from "vue3-colorpicker/types/utils/color";
import type { Lang } from "vue3-colorpicker/types/lang";

export interface ColorPickerProps {
    options: ColorInputOption[];
    isWidget?: boolean;
    pickerType?: "fk" | "chrome";
    size?: "sm" | "2xs" | "xs" | "md" | "lg" | "xl";
    shape?: "circle" | "square";
    format?: ColorFormat;
    disableAlpha?: boolean;
    disableHistory?: boolean;
    roundHistory?: boolean;
    useType?: "pure" | "gradient" | "both";
    activeKey?: "pure" | "gradient";
    lang?: Lang;
    zIndex?: number;
    debounce?: number;
    theme?: "white" | "black";
    blurClose?: boolean;
    defaultPopup?: boolean;
}

withDefaults(defineProps<ColorPickerProps>(), {
    size: "sm",
    isWidget: false,
    pickerType: "chrome",
    shape: "square",
    disableAlpha: false,
    disableHistory: false,
    roundHistory: false,
    format: "hex",
    useType: "pure",
    activeKey: "pure",
    lang: "En",
    zIndex: 9999,
    debounce: 100,
    theme: "white",
    blurClose: false,
    defaultPopup: false,
});

interface ColorInputOption {
  label: ColorInputWithoutInstance,
  value: ColorInputWithoutInstance,
  default_value: ColorInputWithoutInstance,
}

const pureColor = defineModel<ColorInputOption>("pureColor", {
    default: () => ({
      label: "#000000",
      value: "#000000",
      default_value: "#000000"
    }),
});

const gradientColor = defineModel<ColorInputOption>("gradientColor", {
  default: () => ({
    label: "linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 100%)",
    value: "linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 100%)",
    default_value: "linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 100%)"
  }),
});

const picker = ref<{ $el: HTMLDivElement }>();

function open() {
    picker.value?.$el?.parentNode
        .querySelector<HTMLDivElement>(".vc-color-wrap .current-color")
        ?.click();
}

const search = ref<string>();

const query = computed({
  get: () => search.value,
  set: (query) => {
    const regex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

    if(query && regex.test(query)) {
      pureColor.value = { label: query, value: query, default_value: query}
    }

    search.value = query;
  }
})
</script>

<template>
    <UInputMenu
        :options="options"
        v-model="pureColor"
        v-model:query="query"
        :size="size"
        :ui="{ icon: { leading: { pointer: '' } } }"
        :class="{ hidden: $slots.default }"
    >
        <template #leading>
            <UButton
                ref="picker"
                variant="none"
                :padded="false"
                class="absolute h-full top-0 left-0 z-10 px-3"
                @click.stop="open"
            >
                <FontAwesomeIcon
                    class="drop-shadow pointer-events-none"
                    :icon="faCircle"
                    :style="{ color: pureColor.default_value }"
                    :size="
                        {
                            sm: 'md',
                            '2xs': 'xs',
                            xs: 'sm',
                            md: 'lg',
                            lg: 'xl',
                            xl: '2xl',
                        }[size] ?? size
                    "
                />
            </UButton>
            <color-picker
                :pure-color="pureColor.value"
                @update:pure-color="pureColor = { label: $event, value: $event, default_value: $event}"
                :gradient-color="gradientColor.value"
                @update:gradient-color="gradientColor = { label: $event, value: $event, default_value: $event}"
                :is-widget="isWidget"
                :picker-type="pickerType"
                :shape="shape"
                :disable-alpha="disableAlpha"
                :disable-history="disableHistory"
                :round-history="roundHistory"
                :format="format"
                :use-type="useType"
                :active-key="activeKey"
                :lang="lang"
                :z-index="zIndex"
                :picker-picker="picker"
                :debounce="debounce"
                :theme="theme"
                :blur-close="blurClose"
                :default-popup="defaultPopup"
            />
        </template>
        <template #option="{ option }">
          <FontAwesomeIcon
              class="drop-shadow"
              :icon="faCircle"
              :style="{ color: option.default_value ?? option.value }"
              :size="
                        {
                            sm: 'md',
                            '2xs': 'xs',
                            xs: 'sm',
                            md: 'lg',
                            lg: 'xl',
                            xl: '2xl',
                        }[size] ?? size
                    "
          />
          {{ option.label }}
        </template>
    </UInputMenu>
    <slot
        name="default"
        :open="open"
        :pure-color="pureColor"
        :gradient-color="gradientColor"
    />
</template>

<style>
.current-color,
.vc-color-wrap {
    opacity: 0;
}
</style>
